import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  green: '#a7ff83',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

function detectZone(remarks) {
  if (!remarks) return 'unknown';
  const r = remarks.toUpperCase();
  if (r.startsWith('TRAY')) return 'tray';
  if (r.startsWith('TUB')) return 'tub';
  if (r.startsWith('WD')) return 'wd';
  if (r.startsWith('MF')) return 'mf';
  if (r.startsWith('BINDER')) return 'binder';
  if (/^[A-Z]{2}\d{3}$/.test(r)) return 'bin';
  return 'unknown';
}

// Convert BrickStore single-letter type to BrickLink API type
function toBrickLinkType(code) {
  const map = { P: 'PART', M: 'MINIFIG', S: 'SET', G: 'GEAR', B: 'BOOK', C: 'CATALOG', I: 'INSTRUCTION', O: 'ORIGINAL_BOX', U: 'UNSORTED_LOT' };
  const upper = (code || 'P').toUpperCase();
  return map[upper] || 'PART';
}

function getImageUrl(type, partNo, colorId) {
  if (type === 'MINIFIG' || type === 'M') {
    return `https://img.bricklink.com/ItemImage/MN/0/${partNo}.png`;
  }
  return `https://img.bricklink.com/ItemImage/PN/${colorId}/${partNo}.png`;
}

function Upload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // Load saved preview on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ba_upload_preview');
      if (saved) {
        const data = JSON.parse(saved);
        setParsed({ merge: data.merge, colocate: data.colocate, newLots: data.newLots, totalParsed: data.totalParsed });
        setFileName(data.fileName || 'Saved preview');
        const choices = {};
        data.colocate.forEach((item, i) => {
          choices[i] = { approved: true, selectedBin: item.suggestedBins[0] || '-INCOMING' };
        });
        setColocChoices(choices);
        if (data.merge.length > 0) setTab('merge');
        else if (data.colocate.length > 0) setTab('colocate');
        else setTab('new');
      }
    } catch (e) { /* ignore bad data */ }
  }, []);

  // File state
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [fileData, setFileData] = useState(null);

  // Options
  const [excludeTray, setExcludeTray] = useState(true);

  // Parsed & categorized
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);

  // Co-locate selections
  const [colocChoices, setColocChoices] = useState({});

  // Push state
  const [pushing, setPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState(0);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushResults, setPushResults] = useState(null);

  // Active preview tab
  const [tab, setTab] = useState('merge');

  // ─── File handling ───
  function handleFile(file) {
    if (!file) return;
    setFileName(file.name);
    setParsed(null);
    setPushResults(null);
    setPushStatus(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => setFileData(e.target.result);
    reader.readAsText(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  // ─── Parse XML and categorize ───
  async function parseAndCategorize() {
    if (!fileData) return;
    setParsing(true);
    setError(null);

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(fileData, 'text/xml');
      const parseError = doc.querySelector('parsererror');
      if (parseError) throw new Error('Could not parse XML file. Make sure it is a valid BrickStore/BrickStock file.');

      const items = doc.querySelectorAll('ITEM, Item');
      if (items.length === 0) throw new Error('No items found in file.');

      const xmlParts = [];
      items.forEach(item => {
        const get = (...tags) => {
          const children = item.children;
          for (const tag of tags) {
            for (let c = 0; c < children.length; c++) {
              if (children[c].tagName === tag) {
                return children[c].textContent.trim();
              }
            }
          }
          return '';
        };
        const qty = parseInt(get('QTY') || get('Qty') || '0', 10);
        if (qty <= 0) return;

        const colorId = parseInt(get('ColorID', 'COLOR', 'Color', 'colorid', 'color') || '0', 10);
        const rawType = get('ITEMTYPE', 'ItemType', 'ItemTypeID', 'itemtypeid', 'itemtype') || 'P';
        xmlParts.push({
          partNo: get('ITEMID', 'ItemID', 'itemid'),
          itemType: rawType,
          blType: toBrickLinkType(rawType),
          colorId: colorId,
          colorName: get('COLORNAME', 'ColorName', 'colorname', 'CategoryName') || '',
          qty: qty,
          price: parseFloat(get('PRICE', 'Price', 'price') || '0') || 0,
          condition: get('CONDITION', 'Condition', 'condition') || 'N',
          remarks: get('REMARKS', 'Remarks', 'remarks') || '',
        });
      });

      if (xmlParts.length === 0) throw new Error('No valid parts found (all quantities were 0 or negative).');

      // Fetch current inventory
      const invRes = await fetch(`${PROXY}/api/inventories`);
      const invJson = await invRes.json();
      const inventory = invJson.data || [];

      const exactMap = {};
      const partMap = {};

      inventory.forEach(lot => {
        const pno = (lot.item?.no || '').toUpperCase();
        const cid = lot.color_id || 0;
        const bin = lot.remarks || '';

        const exactKey = `${pno}|${cid}`;
        if (!exactMap[exactKey]) exactMap[exactKey] = [];
        exactMap[exactKey].push(lot);

        if (!partMap[pno]) partMap[pno] = [];
        partMap[pno].push({ bin, colorName: lot.color_name || '', colorId: cid });
      });

      const merge = [];
      const colocate = [];
      const newLots = [];

      xmlParts.forEach((part, idx) => {
        const pnoUpper = part.partNo.toUpperCase();
        const exactKey = `${pnoUpper}|${part.colorId}`;

        if (exactMap[exactKey] && exactMap[exactKey].length > 0) {
          const matchLot = exactMap[exactKey][0];
          merge.push({
            ...part,
            index: idx,
            existingId: matchLot.inventory_id,
            existingQty: matchLot.quantity,
            existingBin: matchLot.remarks || '',
            existingPrice: matchLot.unit_price || '',
            newQty: matchLot.quantity + part.qty,
          });
        } else if (partMap[pnoUpper] && partMap[pnoUpper].length > 0) {
          const bins = partMap[pnoUpper].map(p => p.bin).filter(Boolean);
          const uniqueBins = [...new Set(bins)];

          if (excludeTray) {
            const nonTrayBins = uniqueBins.filter(b => detectZone(b) !== 'tray');
            if (nonTrayBins.length > 0) {
              colocate.push({ ...part, index: idx, suggestedBins: nonTrayBins.sort() });
            } else {
              newLots.push({ ...part, index: idx, assignedBin: '-INCOMING' });
            }
          } else {
            if (uniqueBins.length > 0) {
              colocate.push({ ...part, index: idx, suggestedBins: uniqueBins.sort() });
            } else {
              newLots.push({ ...part, index: idx, assignedBin: '-INCOMING' });
            }
          }
        } else {
          newLots.push({ ...part, index: idx, assignedBin: '-INCOMING' });
        }
      });

      const choices = {};
      colocate.forEach((item, i) => {
        choices[i] = { approved: true, selectedBin: item.suggestedBins[0] || '-INCOMING' };
      });
      setColocChoices(choices);

      setParsed({ merge, colocate, newLots, totalParsed: xmlParts.length });

      if (merge.length > 0) setTab('merge');
      else if (colocate.length > 0) setTab('colocate');
      else setTab('new');

      const saved = { merge, colocate, newLots, totalParsed: xmlParts.length, fileName: fileName };
      localStorage.setItem('ba_upload_preview', JSON.stringify(saved));

    } catch (err) {
      setError(err.message);
    } finally {
      setParsing(false);
    }
  }

  // ─── Push to BrickLink ───
  async function pushToBrickLink() {
    if (!parsed) return;
    setPushing(true);
    setPushProgress(0);
    setPushStatus('Pushing to BrickLink...');

    // Auto-price a lot at 6-month sold-avg × 1.25, matching the item's
    // condition. If the sold guide is empty for that color/condition, fall
    // back to the current-listings (stock) guide. The sale rate (parts 30%,
    // minifigs/sets/gear 20%) is ALWAYS returned, even when we can't find an
    // average — so a lot never loses its sale just because pricing fell back.
    // `priced` is false when no average could be found; the caller then flags
    // the lot for manual pricing instead of silently using the file price.
    async function getAutoPrice(item) {
      const saleRate = (item.blType === 'PART') ? 30 : 20;
      const typeMap = { PART: 'part', MINIFIG: 'minifig', SET: 'set', GEAR: 'gear' };
      const urlType = typeMap[item.blType] || 'part';
      const cond = item.condition || 'N';
      async function avgFor(guide) {
        try {
          const r = await fetch(`${PROXY}/api/price/${urlType}/${item.partNo}?color_id=${item.colorId}&guide_type=${guide}&new_or_used=${cond}`);
          const data = await r.json();
          return parseFloat(data?.data?.avg_price || '0') || 0;
        } catch (e) { return 0; }
      }
      let avg = await avgFor('sold');            // 6-month sold average (preferred)
      if (avg <= 0) avg = await avgFor('stock'); // fall back to current listings
      if (avg > 0) {
        const unit_price = Math.round(avg * 1.25 * 10000) / 10000;
        return { unit_price, sale_rate: saleRate, priced: true };
      }
      return { unit_price: null, sale_rate: saleRate, priced: false };
    }
    const results = { mergeOk: 0, colocOk: 0, newOk: 0, errors: [], needsPricing: [] };
    // Build the price/sale fields for a create payload, flagging lots that
    // couldn't be auto-priced so they're never silently listed at the file's
    // placeholder price.
    async function priceFields(item) {
      const ap = await getAutoPrice(item);
      if (!ap.priced) results.needsPricing.push(`${item.partNo}${item.colorName ? ` · ${item.colorName}` : ''}`);
      return {
        unit_price: ap.priced ? ap.unit_price : item.price,
        sale_rate: ap.sale_rate, // always apply the sale rate
      };
    }

    const approvedColocs = Object.entries(colocChoices).filter(([, v]) => v.approved);
    const rejectedColocs = Object.entries(colocChoices).filter(([, v]) => !v.approved);
    const totalOps = parsed.merge.length + approvedColocs.length + rejectedColocs.length + parsed.newLots.length;
    let done = 0;

    const tick = () => { done++; setPushProgress(Math.round((done / totalOps) * 100)); };

    // 1. Merge existing lots
    for (const item of parsed.merge) {
      setPushStatus(`Merging ${item.partNo} (${item.colorName})...`);
      try {
        const res = await fetch(`${PROXY}/api/inventories/${item.existingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: item.qty }),
        });
        if (!res.ok) throw new Error(await res.text());
        const resData = await res.json();
        if (resData.meta && resData.meta.code && resData.meta.code >= 400) {
          throw new Error(resData.meta.message || resData.meta.description || `BrickLink error ${resData.meta.code}`);
        }
        results.mergeOk++;
      } catch (err) {
        results.errors.push(`Merge ${item.partNo}: ${err.message}`);
      }
      tick();
    }

    // 2. Co-located (approved)
    for (const [idxStr, choice] of approvedColocs) {
      const item = parsed.colocate[parseInt(idxStr, 10)];
      setPushStatus(`Creating ${item.partNo} in ${choice.selectedBin}...`);
      try {
        const pf = await priceFields(item);
        const payload = {
          item: { no: item.partNo, type: item.blType },
          color_id: item.colorId,
          quantity: item.qty,
          unit_price: pf.unit_price,
          sale_rate: pf.sale_rate,
          new_or_used: item.condition,
          remarks: choice.selectedBin,
          is_retain: true,
          is_stock_room: false,
        };
        const res = await fetch(`${PROXY}/api/inventories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
        const resData = await res.json();
        if (resData.meta && resData.meta.code && resData.meta.code >= 400) {
          throw new Error(resData.meta.message || resData.meta.description || `BrickLink error ${resData.meta.code}`);
        }
        results.colocOk++;
      } catch (err) {
        results.errors.push(`Co-locate ${item.partNo}: ${err.message}`);
      }
      tick();
    }

    // 2b. Co-located (rejected → -INCOMING)
    for (const [idxStr] of rejectedColocs) {
      const item = parsed.colocate[parseInt(idxStr, 10)];
      setPushStatus(`Creating ${item.partNo} in -INCOMING...`);
      try {
        const pf = await priceFields(item);
        const payload = {
          item: { no: item.partNo, type: item.blType },
          color_id: item.colorId,
          quantity: item.qty,
          unit_price: pf.unit_price,
          sale_rate: pf.sale_rate,
          new_or_used: item.condition,
          remarks: '-INCOMING',
          is_retain: true,
          is_stock_room: false,
        };
        const res = await fetch(`${PROXY}/api/inventories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
        const resData = await res.json();
        if (resData.meta && resData.meta.code && resData.meta.code >= 400) {
          throw new Error(resData.meta.message || resData.meta.description || `BrickLink error ${resData.meta.code}`);
        }
        results.newOk++;
      } catch (err) {
        results.errors.push(`New ${item.partNo}: ${err.message}`);
      }
      tick();
    }

    // 3. Brand new lots → -INCOMING
    for (const item of parsed.newLots) {
      setPushStatus(`Creating ${item.partNo} (${item.colorName})...`);
      try {
        const pf = await priceFields(item);
        const payload = {
          item: { no: item.partNo, type: item.blType },
          color_id: item.colorId,
          quantity: item.qty,
          unit_price: pf.unit_price,
          sale_rate: pf.sale_rate,
          new_or_used: item.condition,
          remarks: '-INCOMING',
          is_retain: true,
          is_stock_room: false,
        };
        const res = await fetch(`${PROXY}/api/inventories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
        const resData = await res.json();
        if (resData.meta && resData.meta.code && resData.meta.code >= 400) {
          throw new Error(resData.meta.message || resData.meta.description || `BrickLink error ${resData.meta.code}`);
        }
        results.newOk++;
      } catch (err) {
        results.errors.push(`New ${item.partNo}: ${err.message}`);
      }
      tick();
    }

    setPushResults(results);
    localStorage.removeItem('ba_upload_preview');
    setPushing(false);
    setPushStatus(null);
  }

  // ─── Launch Stocking Mode ───
  function launchStocking() {
    const stockingParts = [];

    parsed.merge.forEach(item => {
      stockingParts.push({
        partNo: item.partNo,
        name: item.colorName + ' ' + item.partNo,
        type: item.itemType,
        color: item.colorName,
        colorId: item.colorId,
        qty: item.qty,
        bin: item.existingBin,
      });
    });

    Object.entries(colocChoices).forEach(([idxStr, choice]) => {
      if (!choice.approved) return;
      const item = parsed.colocate[parseInt(idxStr, 10)];
      stockingParts.push({
        partNo: item.partNo,
        name: item.colorName + ' ' + item.partNo,
        type: item.itemType,
        color: item.colorName,
        colorId: item.colorId,
        qty: item.qty,
        bin: choice.selectedBin,
      });
    });

    parsed.newLots.forEach(item => {
      stockingParts.push({
        partNo: item.partNo,
        name: item.colorName + ' ' + item.partNo,
        type: item.itemType,
        color: item.colorName,
        colorId: item.colorId,
        qty: item.qty,
        bin: '-INCOMING',
      });
    });

    if (stockingParts.length === 0) return;

    // Check if a stocking session is already in progress
    let finalParts = stockingParts;
    let finalSource = 'XML Upload';
    try {
      const existingRaw = localStorage.getItem('stockingParts');
      if (existingRaw) {
        const existing = JSON.parse(existingRaw);
        if (Array.isArray(existing) && existing.length > 0) {
          const choice = window.confirm(
            `You have ${existing.length} part(s) still waiting in an existing stocking session.\n\n` +
            `Click OK to ADD these ${stockingParts.length} new part(s) to that session.\n` +
            `Click Cancel to START FRESH (your old session will be discarded).`
          );
          if (choice) {
            finalParts = [...existing, ...stockingParts];
            const prevSource = localStorage.getItem('stockingSource') || 'XML Upload';
            finalSource = prevSource.includes('+') ? prevSource : `${prevSource} + XML Upload`;
          }
        }
      }
    } catch (e) { /* if existing data is bad, just overwrite */ }

    localStorage.setItem('stockingParts', JSON.stringify(finalParts));
    localStorage.setItem('stockingSource', finalSource);
    navigate('/stocking');
  }

  // ─── Render: Push Results ───
  if (pushResults) {
    const total = pushResults.mergeOk + pushResults.colocOk + pushResults.newOk;
    const hasErrors = pushResults.errors.length > 0;
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Upload XML" icon="↑" />
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: hasErrors ? '#ea580c' : colors.greenDark }}>
            {hasErrors ? '⚠' : '✓'}
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: hasErrors ? '#ea580c' : colors.greenDark, marginBottom: '16px' }}>
            {hasErrors ? 'Upload Complete with Errors' : 'Upload Complete!'}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {pushResults.mergeOk > 0 && (
              <div style={{ fontSize: '0.85rem', color: colors.textPrimary }}>
                <span style={{ fontWeight: 800, color: '#2563eb' }}>{pushResults.mergeOk}</span> merged
              </div>
            )}
            {pushResults.colocOk > 0 && (
              <div style={{ fontSize: '0.85rem', color: colors.textPrimary }}>
                <span style={{ fontWeight: 800, color: '#7c3aed' }}>{pushResults.colocOk}</span> co-located
              </div>
            )}
            {pushResults.newOk > 0 && (
              <div style={{ fontSize: '0.85rem', color: colors.textPrimary }}>
                <span style={{ fontWeight: 800, color: colors.greenDark }}>{pushResults.newOk}</span> new in -INCOMING
              </div>
            )}
          </div>

          {pushResults.errors.length > 0 && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '14px',
              marginBottom: '20px',
              textAlign: 'left',
            }}>
              {pushResults.errors.map((err, i) => (
                <div key={i} style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: '4px' }}>{err}</div>
              ))}
            </div>
          )}

          {pushResults.needsPricing && pushResults.needsPricing.length > 0 && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px',
              padding: '14px', marginBottom: '20px', textAlign: 'left',
            }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#b45309', marginBottom: '6px' }}>
                ⚠ {pushResults.needsPricing.length} lot{pushResults.needsPricing.length === 1 ? '' : 's'} couldn't be auto-priced — set price manually on BrickLink
              </div>
              <div style={{ fontSize: '0.68rem', color: '#92400e', marginBottom: '8px' }}>
                No sold or current-listing average was found (often a rare color/condition). These went in at the file's price, so check them so they don't sell too cheap.
              </div>
              {pushResults.needsPricing.map((p, i) => (
                <div key={i} style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '3px' }}>{p}</div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={launchStocking}
              style={{
                padding: '12px 24px',
                background: colors.greenDark,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              Start Stocking Mode →
            </button>
            <button
              onClick={() => { setFileName(null); setFileData(null); setParsed(null); setPushResults(null); localStorage.removeItem('ba_upload_preview'); }}
              style={{
                padding: '12px 24px',
                background: colors.navy,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              Upload Another File
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Pushing progress ───
  if (pushing) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Upload XML" icon="↑" />
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.8rem', marginBottom: '12px', color: colors.navy }}>⟳</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.textPrimary, marginBottom: '16px' }}>
            {pushStatus || 'Pushing to BrickLink...'}
          </div>
          <div style={{
            width: '100%',
            height: '10px',
            background: '#e2e4e8',
            borderRadius: '5px',
            overflow: 'hidden',
            marginBottom: '8px',
          }}>
            <div style={{
              width: `${pushProgress}%`,
              height: '100%',
              background: colors.greenDark,
              borderRadius: '5px',
              transition: 'width 0.2s ease',
            }} />
          </div>
          <div style={{ fontSize: '0.78rem', color: colors.textSecondary }}>{pushProgress}%</div>
        </div>
      </div>
    );
  }

  // ─── Render: Preview ───
  if (parsed) {
    const tabs = [
      { key: 'merge', label: 'Merge', count: parsed.merge.length, color: '#2563eb' },
      { key: 'colocate', label: 'Co-locate', count: parsed.colocate.length, color: '#7c3aed' },
      { key: 'new', label: 'New → -INCOMING', count: parsed.newLots.length, color: colors.greenDark },
    ];

    const approvedCount = Object.values(colocChoices).filter(v => v.approved).length;
    const totalPush = parsed.merge.length + approvedCount + (parsed.colocate.length - approvedCount) + parsed.newLots.length;

    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Upload XML" icon="↑" />

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '10px',
          marginBottom: '20px',
        }}>
          {[
            { value: parsed.totalParsed, label: 'Parsed', accent: colors.navy },
            { value: parsed.merge.length, label: 'Merge', accent: '#2563eb' },
            { value: parsed.colocate.length, label: 'Co-locate', accent: '#7c3aed' },
            { value: parsed.newLots.length, label: 'New', accent: colors.greenDark },
          ].map((s, i) => (
            <div key={i} style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderBottom: `3px solid ${s.accent}`,
              borderRadius: '6px',
              padding: '14px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.accent }}>{s.value}</div>
              <div style={{ fontSize: '0.68rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          padding: '10px 16px',
          marginBottom: '16px',
          fontSize: '0.82rem',
          color: colors.greenDark,
          fontWeight: 600,
        }}>
          ✓ {fileName}
        </div>

        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: 1,
                padding: '10px 8px',
                background: tab === t.key ? t.color : colors.cardBg,
                color: tab === t.key ? '#fff' : colors.textPrimary,
                border: `1px solid ${tab === t.key ? t.color : colors.border}`,
                borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
              }}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px',
          maxHeight: '50vh',
          overflowY: 'auto',
        }}>
          {tab === 'merge' && (
            parsed.merge.length === 0 ? (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '20px', fontSize: '0.85rem' }}>
                No matching lots to merge.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                {parsed.merge.map((item, i) => (
                  <div key={i} style={{
                    border: `1px solid ${colors.border}`,
                    borderTop: '3px solid #2563eb',
                    borderRadius: '6px',
                    padding: '10px',
                    textAlign: 'center',
                  }}>
                    <img
                      src={getImageUrl(item.itemType, item.partNo, item.colorId)}
                      alt={item.partNo}
                      style={{ width: '100%', maxHeight: '60px', objectFit: 'contain', marginBottom: '6px' }}
                      onError={e => { e.target.style.opacity = '0.15'; }}
                    />
                    <div style={{ fontSize: '0.75rem', fontWeight: 800, color: colors.textPrimary }}>{item.partNo}</div>
                    <div style={{ fontSize: '0.6rem', color: colors.textSecondary }}>{item.colorName}</div>
                    <div style={{ fontSize: '0.68rem', color: '#2563eb', fontWeight: 700, marginTop: '4px' }}>
                      {item.existingQty} + {item.qty} = {item.newQty}
                    </div>
                    <div style={{ fontSize: '0.58rem', color: colors.textSecondary, marginTop: '2px' }}>
                      ▦ {item.existingBin || '(no bin)'}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'colocate' && (
            parsed.colocate.length === 0 ? (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '20px', fontSize: '0.85rem' }}>
                No co-location candidates.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '10px' }}>
                {parsed.colocate.map((item, i) => {
                  const choice = colocChoices[i] || { approved: true, selectedBin: item.suggestedBins[0] };
                  return (
                    <div key={i} style={{
                      border: `1px solid ${choice.approved ? '#c4b5fd' : colors.border}`,
                      borderTop: `3px solid ${choice.approved ? '#7c3aed' : '#d1d5db'}`,
                      borderRadius: '6px',
                      padding: '10px',
                      textAlign: 'center',
                      opacity: choice.approved ? 1 : 0.5,
                    }}>
                      <img
                        src={getImageUrl(item.itemType, item.partNo, item.colorId)}
                        alt={item.partNo}
                        style={{ width: '100%', maxHeight: '60px', objectFit: 'contain', marginBottom: '6px' }}
                        onError={e => { e.target.style.opacity = '0.15'; }}
                      />
                      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: colors.textPrimary }}>{item.partNo}</div>
                      <div style={{ fontSize: '0.6rem', color: colors.textSecondary }}>{item.colorName} x{item.qty}</div>

                      <select
                        value={choice.selectedBin}
                        onChange={e => setColocChoices(prev => ({
                          ...prev,
                          [i]: { ...prev[i], selectedBin: e.target.value },
                        }))}
                        style={{
                          width: '100%',
                          padding: '5px',
                          marginTop: '6px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontFamily: "'Outfit', sans-serif",
                          fontSize: '0.72rem',
                          color: colors.textPrimary,
                          background: '#fff',
                        }}
                      >
                        {item.suggestedBins.map(b => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>

                      <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                        <button
                          onClick={() => setColocChoices(prev => ({
                            ...prev,
                            [i]: { ...prev[i], approved: true },
                          }))}
                          style={{
                            flex: 1,
                            padding: '5px',
                            background: choice.approved ? '#7c3aed' : '#fff',
                            color: choice.approved ? '#fff' : colors.textSecondary,
                            border: `1px solid ${choice.approved ? '#7c3aed' : colors.border}`,
                            borderRadius: '4px',
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          ✓ Co-locate
                        </button>
                        <button
                          onClick={() => setColocChoices(prev => ({
                            ...prev,
                            [i]: { ...prev[i], approved: false },
                          }))}
                          style={{
                            flex: 1,
                            padding: '5px',
                            background: !choice.approved ? '#ea580c' : '#fff',
                            color: !choice.approved ? '#fff' : colors.textSecondary,
                            border: `1px solid ${!choice.approved ? '#ea580c' : colors.border}`,
                            borderRadius: '4px',
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          → -INCOMING
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'new' && (
            parsed.newLots.length === 0 ? (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '20px', fontSize: '0.85rem' }}>
                No brand new parts — everything matched existing inventory.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                {parsed.newLots.map((item, i) => (
                  <div key={i} style={{
                    border: `1px solid ${colors.border}`,
                    borderTop: `3px solid ${colors.greenDark}`,
                    borderRadius: '6px',
                    padding: '10px',
                    textAlign: 'center',
                  }}>
                    <img
                      src={getImageUrl(item.itemType, item.partNo, item.colorId)}
                      alt={item.partNo}
                      style={{ width: '100%', maxHeight: '60px', objectFit: 'contain', marginBottom: '6px' }}
                      onError={e => { e.target.style.opacity = '0.15'; }}
                    />
                    <div style={{ fontSize: '0.75rem', fontWeight: 800, color: colors.textPrimary }}>{item.partNo}</div>
                    <div style={{ fontSize: '0.6rem', color: colors.textSecondary }}>{item.colorName} x{item.qty}</div>
                    <div style={{ fontSize: '0.62rem', color: colors.textSecondary, marginTop: '4px' }}>
                      ${item.price} · {item.condition === 'N' ? 'New' : 'Used'}
                    </div>
                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: colors.greenDark, marginTop: '2px' }}>
                      → -INCOMING
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        <button
          onClick={pushToBrickLink}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: colors.navy,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 800,
            fontFamily: "'Outfit', sans-serif",
            cursor: 'pointer',
            marginBottom: '10px',
          }}
        >
          Confirm &amp; Push {totalPush} lots to BrickLink
        </button>

        <button
          onClick={() => { setParsed(null); setFileName(null); setFileData(null); localStorage.removeItem('ba_upload_preview'); }}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: colors.cardBg,
            color: colors.textSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            fontSize: '0.85rem',
            fontWeight: 600,
            fontFamily: "'Outfit', sans-serif",
            cursor: 'pointer',
          }}
        >
          Cancel &amp; Start Over
        </button>
      </div>
    );
  }

  // ─── Render: File select ───
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Upload XML" icon="↑" />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '10px',
        marginBottom: '20px',
      }}>
        {[
          { label: 'Supported Format', value: 'BSX', accent: colors.navy },
          { label: 'Also Accepts', value: 'XML', accent: colors.navy },
          { label: 'Preview Before', value: 'Push', accent: colors.greenDark },
        ].map((s, i) => (
          <div key={i} style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderBottom: `3px solid ${s.accent}`,
            borderRadius: '6px',
            padding: '14px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.accent }}>{s.value}</div>
            <div style={{ fontSize: '0.68rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          background: dragOver ? '#f0fdf4' : fileName ? '#f0fdf4' : colors.cardBg,
          border: `2px dashed ${dragOver ? colors.greenDark : fileName ? colors.greenDark : colors.border}`,
          borderRadius: '6px',
          padding: '50px 20px',
          textAlign: 'center',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.bsx"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
        <div style={{
          fontSize: '2rem',
          marginBottom: '12px',
          color: fileName ? colors.greenDark : colors.navy,
        }}>
          {fileName ? '✓' : '↑'}
        </div>
        <div style={{
          fontSize: '1.1rem',
          fontWeight: 800,
          color: fileName ? colors.greenDark : colors.textPrimary,
          marginBottom: '6px',
        }}>
          {fileName ? fileName : 'Drop BrickStore XML here'}
        </div>
        <div style={{
          fontSize: '0.8rem',
          color: colors.textSecondary,
        }}>
          {fileName ? 'Click to choose a different file' : 'or click to browse · .xml or .bsx'}
        </div>
      </div>

      <div style={{
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: '6px',
        padding: '18px 20px',
        marginBottom: '16px',
      }}>
        <div style={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: '14px',
        }}>Upload Options</div>

        <div
          onClick={() => setExcludeTray(!excludeTray)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
          }}
        >
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            border: `2px solid ${excludeTray ? colors.navy : colors.border}`,
            background: excludeTray ? colors.navy : 'transparent',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
          }}>
            {excludeTray && (
              <div style={{ color: '#ffffff', fontSize: '0.7rem', fontWeight: 800 }}>✓</div>
            )}
          </div>
          <div style={{
            fontSize: '0.85rem',
            color: colors.textPrimary,
            fontWeight: excludeTray ? 600 : 400,
          }}>Exclude trays from co-location suggestions</div>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderLeft: '3px solid #dc2626',
          borderRadius: '6px',
          padding: '14px 16px',
          marginBottom: '16px',
          fontSize: '0.82rem',
          color: '#dc2626',
        }}>
          {error}
        </div>
      )}

      <button
        onClick={parseAndCategorize}
        disabled={!fileName || parsing}
        style={{
          width: '100%',
          padding: '14px 24px',
          background: fileName && !parsing ? colors.navy : colors.cardBg,
          color: fileName && !parsing ? '#ffffff' : colors.textSecondary,
          border: fileName && !parsing ? 'none' : `1px solid ${colors.border}`,
          borderRadius: '6px',
          fontSize: '1rem',
          fontWeight: 800,
          fontFamily: "'Outfit', sans-serif",
          cursor: fileName && !parsing ? 'pointer' : 'not-allowed',
          marginBottom: '16px',
        }}
      >
        {parsing ? 'Analyzing file...' : fileName ? `Preview ${fileName}` : 'Select a file to upload'}
      </button>

      <div style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderLeft: '3px solid #2563eb',
        borderRadius: '6px',
        padding: '14px 16px',
        fontSize: '0.78rem',
        color: '#1e40af',
        lineHeight: 1.6,
      }}>
        Supports BrickStore / BrickStock XML format (.bsx or .xml). Matching parts merge with existing lots. Same-part different-color suggests co-location. Brand new parts go to -INCOMING.
      </div>
    </div>
  );
}

export default Upload;